import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Upload,
  FileText,
  Image,
  X,
  CheckCircle,
  AlertTriangle,
  Loader2,
  Clipboard,
} from 'lucide-react';
import { Modal } from '@/components/common';
import { documents as docsApi } from '@/utils/api';

interface FileEntry {
  id: string;
  file: File;
  name: string;
  size: number;
  status: 'queued' | 'uploading' | 'done' | 'error' | 'duplicate';
  progress: number;
  error?: string;
  duplicateInfo?: any;
}

const ALLOWED_TYPES = ['.pdf', '.jpg', '.jpeg', '.png', '.txt', '.doc', '.docx'];
const ALLOWED_MIME = ['application/pdf', 'image/jpeg', 'image/png', 'text/plain',
  'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
const MAX_SIZE = 100 * 1024 * 1024;

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(name: string) {
  const ext = name.toLowerCase().split('.').pop();
  if (ext === 'pdf') return <FileText className="w-5 h-5 text-red-500" />;
  if (['jpg', 'jpeg', 'png'].includes(ext || '')) return <Image className="w-5 h-5 text-blue-500" />;
  if (['doc', 'docx'].includes(ext || '')) return <FileText className="w-5 h-5 text-blue-700" />;
  return <FileText className="w-5 h-5 text-gray-500" />;
}

function isAllowedFile(file: File): boolean {
  const ext = '.' + file.name.toLowerCase().split('.').pop();
  return ALLOWED_TYPES.includes(ext) || ALLOWED_MIME.includes(file.type);
}

let entryCounter = 0;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onUploadsComplete: () => void;
}

export default function DocumentUploadModal({ isOpen, onClose, onUploadsComplete }: Props) {
  const [files, setFiles] = useState<FileEntry[]>([]);
  const [dragOver, setDragOver] = useState(false);
  // Erkannte Rechnungen direkt in die Buchhaltung uebernehmen. Standard: aus —
  // seit Juli 2026 entscheidet das Backoffice bewusst, was gebucht wird.
  const [autoBook, setAutoBook] = useState(false);
  const autoBookRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);
  const isUploading = files.some(f => f.status === 'uploading');
  const hasQueued = files.some(f => f.status === 'queued');

  // Uploads starten sofort beim Hinzufuegen — der Ref haelt den Wert unabhaengig
  // von der Closure des Render-Zyklus, in dem uploadFile erzeugt wurde.
  useEffect(() => { autoBookRef.current = autoBook; }, [autoBook]);

  // Stats
  const doneCount = files.filter(f => f.status === 'done').length;
  const errorCount = files.filter(f => f.status === 'error' || f.status === 'duplicate').length;
  const totalCount = files.length;

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setFiles([]);
      setDragOver(false);
      setAutoBook(false);
      autoBookRef.current = false;
    }
  }, [isOpen]);

  // Add files to queue — abgelehnte Dateien nicht still verwerfen, sondern
  // mit Grund in der Liste anzeigen (Bugtracker #108)
  const addFiles = useCallback((newFiles: File[]) => {
    const entries: FileEntry[] = [];
    for (const file of newFiles) {
      let rejectReason: string | null = null;
      if (!isAllowedFile(file)) {
        rejectReason = 'Dateityp nicht erlaubt';
      } else if (file.size > MAX_SIZE) {
        rejectReason = 'Datei zu groß (max. 100 MB)';
      } else if (file.size === 0) {
        rejectReason = 'Datei ist leer oder nicht lesbar';
      }
      entries.push({
        id: `file-${++entryCounter}`,
        file,
        name: file.name,
        size: file.size,
        status: rejectReason ? 'error' : 'queued',
        progress: rejectReason ? 100 : 0,
        error: rejectReason || undefined,
      });
    }
    if (entries.length > 0) {
      setFiles(prev => [...prev, ...entries]);
    }
  }, []);

  // Ctrl+V paste handler
  useEffect(() => {
    if (!isOpen) return;
    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const pastedFiles: File[] = [];
      for (const item of Array.from(items)) {
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) pastedFiles.push(file);
        }
      }
      if (pastedFiles.length > 0) {
        e.preventDefault();
        addFiles(pastedFiles);
      }
    };
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [isOpen, addFiles]);

  // Drag & Drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  };

  // File input handler
  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files ? Array.from(e.target.files) : [];
    addFiles(selected);
    if (e.target) e.target.value = '';
  };

  // Remove file from queue
  const removeFile = (id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  };

  // Upload a single file
  const uploadFile = async (entry: FileEntry) => {
    setFiles(prev => prev.map(f => f.id === entry.id ? { ...f, status: 'uploading', progress: 30 } : f));

    try {
      const formData = new FormData();
      formData.append('file', entry.file);
      if (autoBookRef.current) formData.append('auto_book', '1');

      await docsApi.upload(formData);

      setFiles(prev => prev.map(f => f.id === entry.id ? { ...f, status: 'done', progress: 100 } : f));
    } catch (err: any) {
      if (err.response?.status === 409 && err.response?.data?.duplicate) {
        setFiles(prev => prev.map(f => f.id === entry.id ? {
          ...f,
          status: 'duplicate',
          progress: 100,
          duplicateInfo: err.response.data.existingDocument,
          error: 'Duplikat',
        } : f));
      } else {
        setFiles(prev => prev.map(f => f.id === entry.id ? {
          ...f,
          status: 'error',
          progress: 100,
          // err.message traegt seit dem Interceptor-Fix (#120) den Server-Grund,
          // auch wenn die Response mal fehlt (Netzwerk-/Timeout-Fehler).
          error: err.response?.data?.error || err.message || 'Upload fehlgeschlagen',
        } : f));
      }
    }
  };

  // Start all queued uploads in parallel
  const startUploads = useCallback(async () => {
    const queued = files.filter(f => f.status === 'queued');
    if (queued.length === 0) return;

    // Upload all in parallel
    await Promise.allSettled(queued.map(entry => uploadFile(entry)));

    // Notify parent to refresh
    onUploadsComplete();
  }, [files, onUploadsComplete]);

  // Auto-start uploads when files are added
  useEffect(() => {
    if (hasQueued && !isUploading) {
      startUploads();
    }
  }, [files.length]);

  // Close handler
  const handleClose = () => {
    if (isUploading) {
      if (!window.confirm('Uploads laufen noch. Wirklich schließen?')) return;
    }
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Dokumente hochladen" size="xl">
      <div className="p-4">
        {/* Top row: Filename + Durchsuchen */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-500 min-w-0">
            <FileText className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">
              {files.length === 0
                ? 'Keine Datei ausgewählt'
                : files.length === 1
                  ? files[0].name
                  : `${files.length} Dateien ausgewählt`
              }
            </span>
          </div>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors flex-shrink-0"
          >
            Durchsuchen
          </button>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept={ALLOWED_TYPES.join(',')}
            multiple
            onChange={handleFileInput}
          />
        </div>

        {/* Buchhaltungs-Option — muss VOR dem Hinzufuegen gesetzt werden,
            weil die Uploads sofort starten */}
        <label
          className={`flex items-start gap-3 mb-4 px-3 py-2.5 rounded-lg border transition-colors ${
            files.length > 0
              ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-70'
              : 'border-gray-200 bg-white hover:bg-gray-50 cursor-pointer'
          }`}
        >
          <input
            type="checkbox"
            checked={autoBook}
            disabled={files.length > 0}
            onChange={(e) => setAutoBook(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm">
            <span className="font-medium text-gray-800">Erkannte Rechnungen direkt in die Buchhaltung übernehmen</span>
            <span className="block text-xs text-gray-500 mt-0.5">
              {files.length > 0
                ? 'Nicht mehr änderbar — die Uploads laufen bereits.'
                : 'Ohne Haken landen Rechnungen nur in der Dokumentenablage und werden dort zum Übernehmen markiert.'}
            </span>
          </span>
        </label>

        {/* Main area: Drag & Drop (left) + File list (right) */}
        <div className="flex gap-4" style={{ minHeight: 320 }}>
          {/* Left: Drag & Drop zone */}
          <div
            ref={dropRef}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`flex-1 border-2 border-dashed rounded-xl flex flex-col items-center justify-center cursor-pointer transition-all ${
              dragOver
                ? 'border-primary-500 bg-primary-50 scale-[1.01]'
                : 'border-gray-300 hover:border-gray-400 bg-gray-50/50'
            }`}
          >
            <Upload className={`w-12 h-12 mb-3 ${dragOver ? 'text-primary-500' : 'text-gray-300'}`} />
            <p className="font-medium text-gray-600 text-sm">Dateien hierher ziehen</p>
            <p className="text-xs text-gray-400 mt-1">oder klicken zum Auswählen</p>
            <div className="flex items-center gap-2 mt-4 text-xs text-gray-400">
              <Clipboard className="w-3 h-3" />
              <span>Strg+V zum Einfügen</span>
            </div>
            <p className="text-xs text-gray-300 mt-2">
              PDF, JPG, PNG, TXT, DOC, DOCX — max. 100 MB
            </p>
          </div>

          {/* Right: File list */}
          <div className="flex-1 flex flex-col border border-gray-200 rounded-xl overflow-hidden bg-white">
            {/* Header with progress */}
            <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <span className="text-xs font-medium text-gray-600">
                {totalCount === 0
                  ? 'Keine Dateien'
                  : `${doneCount} von ${totalCount} hochgeladen`
                }
                {errorCount > 0 && (
                  <span className="text-amber-600 ml-1">({errorCount} Fehler)</span>
                )}
              </span>
              {files.length > 0 && !isUploading && (
                <button
                  onClick={() => setFiles([])}
                  className="text-xs text-gray-400 hover:text-gray-600"
                >
                  Alle entfernen
                </button>
              )}
            </div>

            {/* Progress bar */}
            {totalCount > 0 && (
              <div className="h-1 bg-gray-100">
                <div
                  className="h-full bg-primary-500 transition-all duration-300"
                  style={{ width: `${totalCount > 0 ? (doneCount / totalCount) * 100 : 0}%` }}
                />
              </div>
            )}

            {/* File entries */}
            <div className="flex-1 overflow-auto p-2 space-y-1">
              {files.length === 0 ? (
                <div className="flex-1 flex items-center justify-center h-full text-gray-300 text-sm text-center px-4">
                  <div>
                    <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>Dateien erscheinen hier</p>
                  </div>
                </div>
              ) : (
                files.map(entry => (
                  <div
                    key={entry.id}
                    className={`flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm transition-colors ${
                      entry.status === 'done' ? 'bg-green-50' :
                      entry.status === 'duplicate' ? 'bg-amber-50' :
                      entry.status === 'error' ? 'bg-red-50' :
                      entry.status === 'uploading' ? 'bg-blue-50' :
                      'bg-gray-50'
                    }`}
                  >
                    {/* Icon */}
                    <div className="flex-shrink-0">
                      {entry.status === 'done' ? <CheckCircle className="w-4 h-4 text-green-500" /> :
                       entry.status === 'duplicate' ? <AlertTriangle className="w-4 h-4 text-amber-500" /> :
                       entry.status === 'error' ? <AlertTriangle className="w-4 h-4 text-red-500" /> :
                       entry.status === 'uploading' ? <Loader2 className="w-4 h-4 text-blue-500 animate-spin" /> :
                       getFileIcon(entry.name)
                      }
                    </div>

                    {/* Name + Size */}
                    <div className="flex-1 min-w-0">
                      <p className="truncate text-xs font-medium text-gray-700">{entry.name}</p>
                      <p className="text-[10px] text-gray-400">
                        {formatSize(entry.size)}
                        {entry.status === 'duplicate' && (
                          <span className="text-amber-600 ml-1">
                            — Duplikat{entry.duplicateInfo?.title ? ` von „${entry.duplicateInfo.title}"` : ' (bereits hochgeladen)'}
                          </span>
                        )}
                        {entry.status === 'error' && (
                          <span className="text-red-600 ml-1">— {entry.error}</span>
                        )}
                      </p>
                    </div>

                    {/* Remove button (only for queued or finished) */}
                    {entry.status !== 'uploading' && (
                      <button
                        onClick={() => removeFile(entry.id)}
                        className="flex-shrink-0 p-0.5 text-gray-300 hover:text-gray-500"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
          <p className="text-xs text-gray-400">
            Strg+V, Drag & Drop oder Durchsuchen
          </p>
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Schließen
          </button>
        </div>
      </div>
    </Modal>
  );
}
